// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartEvent,
  ChartOptions,
  Point,
  PointElement,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  Filler,
  TimeUnit,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";

import { Aggregation } from "graphql/api";
import { isSet } from "shared/utils/utils";
import { TimePeriod } from "types";
import {
  COLOR_SELECT,
  hexToRgba,
  getSiteSelection,
  getGraphBoundaryDatesFromPeriod,
} from "guiutils";
import TrendWindowModel from "model/trendwindow";
import { ActionType, IAction, raiseAction, IActionTypeZoom, IActionTypeSelectIos } from "actions";
import { IState } from "states/states";
import { IInput, IOutput } from "shared/types/parameter";

import { IDropdownSelectOption } from "./DropdownSelect";
import IOsModel from "model/ios";

import IconZoomFit from "icons/iconZoomFit";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// if we have more than this items in the legend, we show it on the right side
const NR_TLS_LEGEND_TOP = 10;

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  zoomPlugin,
  Filler,
);

export type IoChartType = "line" | "bar";
export type IoType = "analog" | "digital";

export interface IIoGraphBaseProps {
  isLoading: boolean;
  queryMoreData: boolean;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
  graphStart: number | undefined;
  graphEnd: number | undefined;
  timePeriod: TimePeriod;
  aggregation: Aggregation;
  minX?: number;
  maxX?: number;
  input: IInput[] | undefined;
  output: IOutput[] | undefined;
}

export const mapBaseStateToProps = (state: IState) => {
  const siteSelection = getSiteSelection(state);
  const { graphStart, graphEnd, timePeriod } = getGraphBoundaryDatesFromPeriod(
    state.gui.ioPeriod,
    state.site.tzOffset,
  );
  const props: IIoGraphBaseProps = {
    isLoading: state.scheduler.isIoTrendLoading,
    queryMoreData: state.trending.queryMoreData,
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
    graphStart,
    graphEnd,
    timePeriod,
    aggregation: TrendWindowModel.getIoAggregation(state),
    minX: state.gui.ioPeriod.zoomMin,
    maxX: state.gui.ioPeriod.zoomMax,
    input: state.para.parameter?.input,
    output: state.para.parameter?.output,
  };
  return props;
};

export interface IIoGraphBaseDispatch {
  onZooming: (minX?: number, maxX?: number) => void;
  onDropdownIOSelectionChanged: (type: IoType, inputIds: number[], outputIds: number[]) => void;
}

export const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoGraphBaseDispatch = {
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "ioPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
    onDropdownIOSelectionChanged: (type, inputIds, outputIds) => {
      const payload: IActionTypeSelectIos = { type, inputIds, outputIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_IOS, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
  loading: {
    id: "Data loading...",
  },
});

// common base of the IO graphs: frame, time axis, zooming/panning and legend selection
abstract class IoGraphBaseComponent<Props> extends React.Component<
  Props & IIoGraphBaseProps & IIoGraphBaseDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  protected abstract readonly ioType: IoType;
  protected readonly compRef = React.createRef<HTMLDivElement>();
  protected selectedIos: IDropdownSelectOption[] = [];
  protected parameter: (IInput | IOutput | undefined)[] = []; // for each selected IO
  protected data: number[][][] = []; // array of [timestamp, y] values for each selected IO
  protected startTime?: number;
  protected endTime?: number;
  private minUnits: TimeUnit = "month";
  private endDateOffset = 0;

  private zoomLock = false;
  private panLock = false;

  // prepare this.data
  protected abstract preProcessData(): void;
  protected abstract createGraphData(): ChartData<IoChartType, ScatterDataPoint[], unknown>;
  protected abstract createGraphOptions(): ChartOptions<IoChartType>;

  protected getChartType(): IoChartType {
    return "line";
  }

  // order of the selected IOs, matching the legend
  protected orderSelectedIos(ios: IDropdownSelectOption[]) {
    return ios;
  }

  public render() {
    if (this.props.isLoading || this.props.queryMoreData) {
      return (
        <div id="iograph" className="graph" ref={this.compRef}>
          <div className="loading-spinner-box">
            <div id="loading-spinner"></div>
            <div id="loading-text">
              <FormattedMessage id={messages.loading.id} />
            </div>
          </div>
        </div>
      );
    }

    this.prepareSelectedIos();
    this.prepareTimeRange();
    this.preProcessData();

    return (
      <div id="iograph" className="graph" ref={this.compRef}>
        {isSet(this.props.minX) || isSet(this.props.maxX) ?
          <div
            className="graph-zoom"
            onClick={() => this.props.onZooming()}
            title={this.context.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        : <></>}
        <Chart
          type={this.getChartType()}
          data={this.createGraphData()}
          options={this.createGraphOptions()}
        />
      </div>
    );
  }

  private prepareSelectedIos() {
    const allIos = IOsModel.getAllAvailableIos(
      [this.props.input ?? [], this.props.output ?? []],
      this.ioType,
    );
    const selectedIos = IOsModel.getSelectedIos(
      allIos,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      true,
    );
    this.selectedIos = this.orderSelectedIos([...selectedIos[0], ...selectedIos[1]]);
    this.parameter = this.selectedIos.map((curItem) =>
      curItem.source === "input" ?
        this.props.input?.[curItem.index]
      : this.props.output?.[curItem.index],
    );
  }

  private prepareTimeRange() {
    this.startTime = this.props.minX ? this.props.minX / 1000 : this.props.graphStart;
    this.endTime = this.props.maxX ? this.props.maxX / 1000 : this.props.graphEnd;
    // IO data is loaded for the period (not the zoom range), so render in that aggregation
    this.endDateOffset =
      [Aggregation.MONTH, Aggregation.DAY].includes(this.props.aggregation) ? 1 : 0;
    this.minUnits = TrendWindowModel.getTimeUnit(this.props.aggregation);
  }

  // ---------- chart options shared by all IO graphs ----------

  protected createTimeScale() {
    return {
      type: "time" as const,
      time: {
        minUnit: this.minUnits,
        displayFormats: {
          minute: this.context.formatMessage(messages.graphFormatAxisMinute),
          hour: this.context.formatMessage(messages.graphFormatAxisHour),
          day: this.context.formatMessage(messages.graphFormatAxisDay),
        },
      },
      min: this.startTime ? this.startTime * 1000 : undefined,
      max: this.endTime ? this.endTime * 1000 - this.endDateOffset : undefined,
      stacked: true, // only relevant for bar chart
    };
  }

  protected isLegendTop() {
    return this.selectedIos.length <= NR_TLS_LEGEND_TOP;
  }

  protected createTooltipTitle(tooltipItems: TooltipItem<IoChartType>[]) {
    const date = new Date(tooltipItems[0].parsed.x ?? 0);
    const showTime =
      this.props.timePeriod !== TimePeriod.YEAR && this.props.timePeriod !== TimePeriod.MONTH;
    const localeOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: this.props.timePeriod !== TimePeriod.YEAR ? "numeric" : undefined,
      hour: showTime ? "numeric" : undefined,
      minute: showTime ? "numeric" : undefined,
    };
    return date.toLocaleString(this.context.locale, localeOptions);
  }

  // the controller offers fixed periods only, without zooming and panning
  protected createZoomOptions(): NonNullable<ChartOptions<IoChartType>["plugins"]>["zoom"] {
    return {
      pan: {
        enabled: IS_CLOUD,
        mode: "x",
        modifierKey: "shift",
        onPanStart: (context) => this.panStartCallback(context.chart, context.point),
        onPanComplete: () => (this.zoomLock = false),
        onPanRejected: () => (this.zoomLock = false),
      },
      zoom: {
        mode: "x",
        wheel: {
          enabled: IS_CLOUD,
        },
        drag: {
          enabled: IS_CLOUD,
          borderColor: hexToRgba(COLOR_SELECT, 0.7),
          backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
        },
        pinch: {
          enabled: IS_CLOUD,
        },
        onZoom: (context) =>
          this.props.onZooming(context.chart.scales.x.min, context.chart.scales.x.max),
        onZoomStart: (context) => this.zoomCheck(context.chart, context.point),
        onZoomComplete: () => (this.panLock = false),
        onZoomRejected: () => (this.panLock = false),
      },
      limits: {
        x: {
          min: "original",
          max: "original",
        },
      },
    };
  }

  private isInChartArea(chart: ChartJS, point: Point) {
    return (
      point.x !== null &&
      point.y !== null &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom
    );
  }

  private zoomCheck(chart: ChartJS, point: Point) {
    this.panLock = !this.zoomLock && this.isInChartArea(chart, point);
    return this.panLock;
  }

  private panStartCallback(chart: ChartJS, point: Point) {
    this.zoomLock =
      !this.panLock &&
      this.props.minX !== undefined &&
      this.props.maxX !== undefined &&
      this.isInChartArea(chart, point);
    return this.zoomLock;
  }

  // legend click toggles an IO; shift-click shows this IO only or all again
  protected legendItemClick(evt: ChartEvent, legendItem: LegendItem) {
    const index = legendItem.datasetIndex;
    if (index === undefined) {
      return;
    }

    let newSelectedIos: IDropdownSelectOption[];
    if ((evt.native as PointerEvent).shiftKey) {
      const anyHidden = this.selectedIos.some((io) => io.visible === false);
      if (legendItem.hidden === false && anyHidden) {
        // show all again
        newSelectedIos = this.selectedIos.map((anItem) => {
          anItem.visible = true;
          return anItem;
        });
      } else {
        // hide all except the current one
        newSelectedIos = this.selectedIos
          .filter((anItem, anIndex) => anIndex === index)
          .map((anItem) => {
            anItem.visible = Boolean(legendItem.hidden);
            return anItem;
          });
      }
    } else {
      newSelectedIos = this.selectedIos
        .map((anItem, anIndex) => {
          if (anIndex === index) {
            anItem.visible = Boolean(legendItem.hidden);
          }
          return anItem;
        })
        .filter((anItem) => anItem.visible);
    }

    this.props.onDropdownIOSelectionChanged(
      this.ioType,
      newSelectedIos.filter((curItem) => curItem.source === "input").map((curItem) => curItem.id),
      newSelectedIos.filter((curItem) => curItem.source === "output").map((curItem) => curItem.id),
    );
  }
}

export default IoGraphBaseComponent;
